<template>
  <v-container fluid class="diag-admin pa-4 pa-md-6">
    <v-alert v-if="!puedeAdministrar" type="error" outlined dense class="mb-4">
      No tienes permiso para ver este panel.
    </v-alert>

    <template v-else>
      <div class="page-header d-flex flex-wrap align-center mb-6">
        <div class="d-flex align-center">
          <div class="page-header__icon">
            <v-icon color="primary" size="28">mdi-monitor-dashboard</v-icon>
          </div>
          <div>
            <h1 class="page-header__title">Diagnóstico del sistema</h1>
            <p class="page-header__subtitle mb-0">Panel administrativo de registros por usuario, dispositivo y fecha</p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn color="primary" small depressed class="rounded-lg" :loading="cargar" @click="cargarDatos">
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>

      <!-- KPIs -->
      <v-row dense class="mb-4">
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card rounded-lg pa-3 text-center">
            <div class="kpi-card__value">{{ kpis.total_registros || 0 }}</div>
            <div class="kpi-card__label">Registros</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card rounded-lg pa-3 text-center">
            <div class="kpi-card__value">{{ kpis.usuarios_unicos || 0 }}</div>
            <div class="kpi-card__label">Usuarios</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card rounded-lg pa-3 text-center">
            <div class="kpi-card__value">{{ kpis.dispositivos_unicos || 0 }}</div>
            <div class="kpi-card__label">Dispositivos</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card kpi-card--green rounded-lg pa-3 text-center">
            <div class="kpi-card__value success--text">{{ kpis.total_verde || 0 }}</div>
            <div class="kpi-card__label">Verde</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card kpi-card--yellow rounded-lg pa-3 text-center">
            <div class="kpi-card__value warning--text">{{ kpis.total_amarillo || 0 }}</div>
            <div class="kpi-card__label">Amarillo</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card flat outlined class="kpi-card kpi-card--red rounded-lg pa-3 text-center">
            <div class="kpi-card__value error--text">{{ kpis.total_rojo || 0 }}</div>
            <div class="kpi-card__label">Rojo</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <v-card flat outlined class="kpi-card rounded-lg pa-3 text-center">
            <div class="kpi-card__value">{{ formatNum(kpis.promedio_speed_mbps) }} Mbps</div>
            <div class="kpi-card__label">Velocidad prom.</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <v-card flat outlined class="kpi-card rounded-lg pa-3 text-center">
            <div class="kpi-card__value">{{ formatNum(kpis.promedio_ping_ms) }} ms</div>
            <div class="kpi-card__label">Ping prom.</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filtros -->
      <v-card flat outlined class="rounded-lg mb-4">
        <v-card-text class="py-3">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filtros.fechaInicio"
                label="Desde"
                type="date"
                dense
                hide-details
                outlined
                class="rounded-lg"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filtros.fechaFin"
                label="Hasta"
                type="date"
                dense
                hide-details
                outlined
                class="rounded-lg"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filtros.iderp"
                label="ID usuario ERP"
                dense
                hide-details
                outlined
                class="rounded-lg"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filtros.deviceId"
                label="ID dispositivo"
                dense
                hide-details
                outlined
                class="rounded-lg"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filtros.semaforo"
                :items="semaforoOptions"
                label="Semáforo"
                dense
                hide-details
                outlined
                clearable
                class="rounded-lg"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filtros.busqueda"
                label="Buscar"
                dense
                hide-details
                outlined
                clearable
                prepend-inner-icon="mdi-magnify"
                class="rounded-lg"
                @keyup.enter="aplicarFiltros"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="12" class="d-flex justify-end">
              <v-btn small outlined color="grey darken-1" class="mr-2 rounded-lg" @click="limpiarFiltros">
                Limpiar
              </v-btn>
              <v-btn small color="primary" depressed class="rounded-lg" @click="aplicarFiltros">
                Filtrar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tabla -->
      <v-card flat outlined class="rounded-lg">
        <v-data-table
          :headers="headers"
          :items="registros"
          :loading="cargar"
          :server-items-length="totalRegistros"
          :options.sync="tableOptions"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          class="elevation-0"
          dense
          @update:options="onTableOptions"
        >
          <template v-slot:item.usuario_nombre_erp="{ item }">
            <div>{{ nombreUsuario(item) }}</div>
            <div class="caption grey--text">ID {{ item.id_usuario_erp || '—' }}</div>
          </template>
          <template v-slot:item.semaforo_general="{ item }">
            <v-chip x-small :color="semaforoColor(item.semaforo_general)" text-color="white">
              {{ semaforoLabel(item.semaforo_general) }}
            </v-chip>
          </template>
          <template v-slot:item.captured_at="{ item }">
            {{ formatFecha(item.captured_at || item.created_at) }}
          </template>
          <template v-slot:item.speed_mbps="{ item }">
            {{ item.speed_mbps != null ? item.speed_mbps + ' Mbps' : '—' }}
          </template>
          <template v-slot:item.ping_ms="{ item }">
            {{ item.ping_ms != null ? item.ping_ms + ' ms' : '—' }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon x-small color="primary" @click="verDetalle(item)">
              <v-icon small>mdi-eye-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Detalle -->
    <v-dialog v-model="dialogDetalle" max-width="1100" scrollable>
      <v-card v-if="detalle">
        <v-card-title class="py-3">
          Registro #{{ detalle.id }}
          <v-spacer></v-spacer>
          <v-chip x-small :color="semaforoColor(detalle.semaforo_general)" text-color="white" class="mr-2">
            {{ semaforoLabel(detalle.semaforo_general) }}
          </v-chip>
          <v-btn icon @click="dialogDetalle = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense>
            <!-- Usuario -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Usuario</div>
                <div class="font-weight-bold">{{ nombreUsuario(detalle) }}</div>
                <div class="caption grey--text">{{ detalle.usuario_email_erp || detalle.usuario_email || '' }}</div>
                <div class="caption">ID ERP (usuarios.id_usuario): {{ detalle.id_usuario_erp || '—' }}</div>
                <div class="caption" v-if="detalle.usuario_nombre && detalle.usuario_nombre !== detalle.usuario_nombre_erp">
                  Nombre capturado: {{ detalle.usuario_nombre }}
                </div>
              </div>
            </v-col>

            <!-- Dispositivo -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Dispositivo</div>
                <div class="detail-mono">{{ detalle.device_id || '—' }}</div>
                <div>{{ detalle.device_platform || '—' }}</div>
                <div class="caption">{{ detalle.device_manufacturer }} · {{ detalle.device_model }}</div>
                <div class="caption">OS: {{ detalle.device_os_version || '—' }}</div>
              </div>
            </v-col>

            <!-- Red -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Conexión a internet</div>
                <div>Estado: {{ detalleOnlineLabel }}</div>
                <div>Tipo efectivo: {{ detalle.network_type || payloadRed.type || '—' }}</div>
                <div>Velocidad medida: {{ detalle.speed_mbps != null ? detalle.speed_mbps + ' Mbps' : '—' }}</div>
                <div v-if="payloadRed.speedDetail">Método medición: {{ payloadRed.speedDetail }}</div>
                <div v-if="payloadRed.browserDownlink != null">Referencia navegador: {{ payloadRed.browserDownlink }} Mbps</div>
                <div>RTT: {{ detalle.rtt_ms != null ? detalle.rtt_ms + ' ms' : '—' }}</div>
                <div>Ping real: {{ detalle.ping_ms != null ? detalle.ping_ms + ' ms' : '—' }}</div>
                <div>Ahorro de datos: {{ payloadRed.saveData ? 'Activado' : 'Desactivado' }}</div>
                <div class="mt-1">
                  <v-chip x-small :color="semaforoColor(detalle.semaforo_internet)" text-color="white" class="mr-1">Internet</v-chip>
                  <v-chip x-small :color="semaforoColor(detalle.semaforo_latencia)" text-color="white">Latencia</v-chip>
                </div>
              </div>
            </v-col>

            <!-- Hardware -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Hardware del equipo</div>
                <div>CPU: {{ detalle.cpu_cores || payloadHardware.cores || '—' }} núcleos</div>
                <div>RAM estimada: {{ detalle.ram_gb != null ? detalle.ram_gb + ' GB' : formatRam(payloadHardware.deviceMemory) }}</div>
                <div>Plataforma: {{ payloadHardware.platform || '—' }}</div>
                <div>Puntos táctiles: {{ payloadHardware.maxTouchPoints != null ? payloadHardware.maxTouchPoints : '—' }}</div>
                <div>Memoria JS: {{ payloadMemoriaJs.used || '—' }} / {{ payloadMemoriaJs.limit || '—' }} ({{ payloadMemoriaJs.usagePercent || detalle.memoria_js_pct + '%' }})</div>
                <div>Almacenamiento navegador: {{ payloadAlmacenamiento.usagePercent || '—' }}</div>
                <div>Libre: {{ detalle.storage_available_mb != null ? detalle.storage_available_mb + ' MB' : '—' }}</div>
                <v-chip x-small :color="semaforoColor(detalle.semaforo_hardware)" text-color="white" class="mt-1">Equipo</v-chip>
              </div>
            </v-col>

            <!-- Pantalla -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Pantalla y ventana</div>
                <div>Resolución: {{ payloadPantalla.width || '—' }} × {{ payloadPantalla.height || '—' }}</div>
                <div>Ventana: {{ payloadPantalla.innerWidth || '—' }} × {{ payloadPantalla.innerHeight || '—' }}</div>
                <div>Color: {{ payloadPantalla.colorDepth || '—' }} bits</div>
                <div>Densidad: {{ payloadPantalla.pixelRatio || '—' }}x</div>
              </div>
            </v-col>

            <!-- Navegador -->
            <v-col cols="12" md="6">
              <div class="detail-block">
                <div class="detail-block__title">Navegador y memoria</div>
                <div>Navegador: {{ payloadNavegador.name || '—' }}</div>
                <div>Idioma: {{ payloadNavegador.language || '—' }}</div>
                <div>Zona horaria: {{ payloadNavegador.timezone || '—' }}</div>
              </div>
            </v-col>

            <!-- Ubicación + mapa -->
            <v-col cols="12">
              <div class="detail-block">
                <div class="detail-block__title">Ubicación</div>
                <div>{{ payloadUbicacion.address || ubicacionTexto(detalle) }}</div>
                <div class="caption mt-1">
                  Coordenadas: {{ formatCoords(detalle.ubicacion_lat, detalle.ubicacion_lng) }}
                  <span v-if="payloadUbicacion.source"> · {{ payloadUbicacion.source === 'gps' ? 'GPS' : 'IP' }}</span>
                </div>
                <div v-if="mapEmbedUrl" class="detail-map-wrap mt-3">
                  <iframe class="detail-map" :src="mapEmbedUrl" title="Mapa de ubicación"></iframe>
                </div>
                <div v-if="externalMapUrl" class="mt-2">
                  <a :href="externalMapUrl" target="_blank" rel="noopener noreferrer" class="text-caption primary--text">
                    Abrir en OpenStreetMap
                  </a>
                </div>
              </div>
            </v-col>

            <!-- Referencia óptima -->
            <v-col cols="12" v-if="referenciaComparativa.length">
              <div class="detail-block">
                <div class="detail-block__title">Referencia óptima vs capturado</div>
                <div class="detail-ref-row detail-ref-row--head">
                  <span>Métrica</span><span>Óptimo</span><span>Capturado</span><span></span>
                </div>
                <div v-for="item in referenciaComparativa" :key="item.key" class="detail-ref-row">
                  <span>{{ item.label }}</span>
                  <span>{{ item.optimo }}</span>
                  <span :class="'text-' + (item.status === 'green' ? 'success' : (item.status === 'yellow' ? 'warning' : 'error'))">{{ item.actual }}</span>
                  <span>
                    <v-icon x-small :color="semaforoColor(item.status)">
                      {{ item.status === 'green' ? 'mdi-check-circle' : (item.status === 'yellow' ? 'mdi-alert-circle' : 'mdi-close-circle') }}
                    </v-icon>
                  </span>
                </div>
              </div>
            </v-col>

            <!-- User agent -->
            <v-col cols="12">
              <div class="detail-block">
                <div class="detail-block__title">Detalle técnico</div>
                <div class="detail-useragent">{{ detalle.user_agent || payloadNavegador.userAgent || '—' }}</div>
                <div class="caption grey--text mt-2">
                  Capturado: {{ formatFecha(detalle.captured_at || detalle.created_at) }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <div class="detail-block__title mb-2">Payload completo</div>
          <pre class="detail-json">{{ detalleJson }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />
  </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'

var ADMIN_IDERP = [1, 334, 568]

export default {
  name: 'DiagnosticoSistemaAdmin',

  components: { Alerta, carga },

  mixins: [validarErrores],

  data () {
    var hoy = new Date()
    var hace7 = new Date()
    hace7.setDate(hoy.getDate() - 7)
    return {
      cargar: false,
      respuestaAlerta: false,
      parametros: { dialogError: false, mensaje: '', color: '' },
      kpis: {},
      registros: [],
      totalRegistros: 0,
      detalle: null,
      detalleJson: '',
      dialogDetalle: false,
      filtros: {
        fechaInicio: hace7.toISOString().slice(0, 10),
        fechaFin: hoy.toISOString().slice(0, 10),
        iderp: '',
        deviceId: '',
        semaforo: null,
        busqueda: '',
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
      },
      semaforoOptions: [
        { text: 'Verde', value: 'green' },
        { text: 'Amarillo', value: 'yellow' },
        { text: 'Rojo', value: 'red' },
      ],
      headers: [
        { text: 'Fecha', value: 'captured_at' },
        { text: 'Usuario', value: 'usuario_nombre_erp' },
        { text: 'Dispositivo', value: 'device_id' },
        { text: 'Semáforo', value: 'semaforo_general', sortable: false },
        { text: 'Velocidad', value: 'speed_mbps' },
        { text: 'Ping', value: 'ping_ms' },
        { text: 'Ciudad', value: 'ubicacion_ciudad' },
        { text: '', value: 'actions', sortable: false, width: 48 },
      ],
    }
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    puedeAdministrar () {
      var u = this.getdatosUsuarioERPERP || {}
      return ADMIN_IDERP.indexOf(Number(u.iderp)) >= 0
    },

    payloadData () {
      return (this.detalle && this.detalle.payload) || {}
    },

    payloadRed () {
      return this.payloadData.red || {}
    },

    payloadHardware () {
      return this.payloadData.hardware || {}
    },

    payloadPantalla () {
      return this.payloadData.pantalla || {}
    },

    payloadNavegador () {
      return this.payloadData.navegador || {}
    },

    payloadMemoriaJs () {
      return this.payloadData.memoriaJs || {}
    },

    payloadAlmacenamiento () {
      return this.payloadData.almacenamiento || {}
    },

    payloadUbicacion () {
      return this.payloadData.ubicacion || {}
    },

    referenciaComparativa () {
      return this.payloadData.referenciaComparativa || []
    },

    detalleOnlineLabel () {
      if (this.detalle && this.detalle.online === 1) return 'En línea'
      if (this.detalle && this.detalle.online === 0) return 'Desconectado'
      if (this.payloadRed.online === true) return 'En línea'
      if (this.payloadRed.online === false) return 'Desconectado'
      return '—'
    },

    mapEmbedUrl () {
      var lat = this.detalle && (this.detalle.ubicacion_lat != null ? this.detalle.ubicacion_lat : this.payloadUbicacion.lat)
      var lng = this.detalle && (this.detalle.ubicacion_lng != null ? this.detalle.ubicacion_lng : this.payloadUbicacion.lng)
      if (lat == null || lng == null) return ''
      var delta = (this.payloadUbicacion.source === 'ip') ? 0.08 : 0.012
      var bbox = (lng - delta) + '%2C' + (lat - delta) + '%2C' + (lng + delta) + '%2C' + (lat + delta)
      return 'https://www.openstreetmap.org/export/embed.html?bbox=' + bbox + '&layer=mapnik&marker=' + lat + '%2C' + lng
    },

    externalMapUrl () {
      var lat = this.detalle && (this.detalle.ubicacion_lat != null ? this.detalle.ubicacion_lat : this.payloadUbicacion.lat)
      var lng = this.detalle && (this.detalle.ubicacion_lng != null ? this.detalle.ubicacion_lng : this.payloadUbicacion.lng)
      if (lat == null || lng == null) return ''
      return 'https://www.openstreetmap.org/?mlat=' + lat + '&mlon=' + lng + '#map=15/' + lat + '/' + lng
    },
  },

  mounted () {
    if (!this.puedeAdministrar) return
    this.cargarDatos()
  },

  methods: {
    queryParams () {
      return {
        fecha_inicio: this.filtros.fechaInicio || undefined,
        fecha_fin: this.filtros.fechaFin || undefined,
        iderp: this.filtros.iderp || undefined,
        device_id: this.filtros.deviceId || undefined,
        semaforo: this.filtros.semaforo || undefined,
        busqueda: this.filtros.busqueda || undefined,
        limit: this.tableOptions.itemsPerPage,
        offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
      }
    },

    cargarDatos () {
      if (!this.puedeAdministrar) return
      this.cargar = true
      var params = this.queryParams()
      Promise.all([
        axios.get('diagnostico-sistema.kpis', { params: params }),
        axios.get('diagnostico-sistema.list', { params: params }),
      ])
        .then((results) => {
          this.kpis = results[0].data || {}
          this.registros = (results[1].data && results[1].data.rows) || []
          this.totalRegistros = (results[1].data && results[1].data.total) || 0
        })
        .catch((error) => {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Error al cargar diagnósticos'
          this.validarError(msg)
        })
        .finally(() => {
          this.cargar = false
        })
    },

    aplicarFiltros () {
      this.tableOptions.page = 1
      this.cargarDatos()
    },

    limpiarFiltros () {
      var hoy = new Date()
      var hace7 = new Date()
      hace7.setDate(hoy.getDate() - 7)
      this.filtros = {
        fechaInicio: hace7.toISOString().slice(0, 10),
        fechaFin: hoy.toISOString().slice(0, 10),
        iderp: '',
        deviceId: '',
        semaforo: null,
        busqueda: '',
      }
      this.aplicarFiltros()
    },

    onTableOptions (options) {
      if (!this.puedeAdministrar) return
      if (!options) return
      var changed = options.page !== this.tableOptions.page ||
        options.itemsPerPage !== this.tableOptions.itemsPerPage
      if (!changed && this.registros.length) return
      this.cargarDatos()
    },

    verDetalle (item) {
      this.cargar = true
      axios.get('diagnostico-sistema.detalle/' + item.id)
        .then((response) => {
          this.detalle = response.data
          this.detalleJson = JSON.stringify(response.data.payload || response.data, null, 2)
          this.dialogDetalle = true
        })
        .catch((error) => {
          var msg = (error.response && error.response.data && error.response.data.message) || 'No se pudo cargar el detalle'
          this.validarError(msg)
        })
        .finally(() => {
          this.cargar = false
        })
    },

    nombreUsuario (row) {
      if (!row) return '—'
      return row.usuario_nombre_erp || row.usuario_nombre || '—'
    },

    formatRam (val) {
      if (val == null || val === '') return '—'
      return val + ' GB'
    },

    formatCoords (lat, lng) {
      if (lat == null || lng == null) return '—'
      return Number(lat).toFixed(5) + ', ' + Number(lng).toFixed(5)
    },

    semaforoColor (val) {
      if (val === 'green') return 'success'
      if (val === 'yellow') return 'warning'
      if (val === 'red') return 'error'
      return 'grey'
    },

    semaforoLabel (val) {
      if (val === 'green') return 'Verde'
      if (val === 'yellow') return 'Amarillo'
      if (val === 'red') return 'Rojo'
      return '—'
    },

    formatFecha (val) {
      if (!val) return '—'
      try {
        return new Date(val).toLocaleString('es-MX')
      } catch (e) {
        return val
      }
    },

    formatNum (val) {
      if (val == null || val === '') return '—'
      return val
    },

    ubicacionTexto (row) {
      return [row.ubicacion_ciudad, row.ubicacion_region, row.ubicacion_pais].filter(Boolean).join(', ') || '—'
    },
  },
}
</script>

<style scoped>
.page-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
}

.page-header__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
}

.page-header__subtitle {
  font-size: 0.85rem;
  color: #6b7280;
}

.kpi-card {
  border-color: #e8ecf1 !important;
  background: #fff;
}

.kpi-card__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.kpi-card__label {
  font-size: 0.72rem;
  color: #8892a6;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 4px;
}

.detail-block {
  background: #f8fafc;
  border: 1px solid #eef1f6;
  border-radius: 10px;
  padding: 12px 14px;
  height: 100%;
}

.detail-block__title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #8892a6;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.detail-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.82rem;
  word-break: break-all;
}

.detail-json {
  background: #1f2937;
  color: #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  font-size: 0.72rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-map-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #eef2f7;
}

.detail-map {
  display: block;
  width: 100%;
  height: 260px;
  border: 0;
}

.detail-useragent {
  font-size: 0.72rem;
  color: #6b7280;
  word-break: break-all;
  line-height: 1.4;
  background: #f3f4f6;
  padding: 10px 12px;
  border-radius: 8px;
}

.detail-ref-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 40px;
  gap: 8px;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #eef1f6;
  font-size: 0.8rem;
}

.detail-ref-row--head {
  font-size: 0.68rem;
  font-weight: 700;
  color: #8892a6;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}
</style>
